import { useNavigate } from 'react-router-dom';
import { Modal, ModalPortal, ModalOverlay, ModalClose, ModalTitle, ModalDescription } from '@klaro7/ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { useAuth } from '../hooks/useAuth';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';

/* Gate mostrato quando un visitatore non registrato clicca "Assistenza".
   Invita ad accedere (primario) o registrarsi (secondario) per scrivere al supporto.
   Separato da LoginModal per non accoppiarne gli stati. */

const SUPPORT_REDIRECT = '/my/supporto';

export function SupportGateModal() {
  const navigate = useNavigate();
  const { showLoginModal, loginModalContext, closeLoginModal, openLoginModal } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const registrationOpen = registrationStatus?.registrationEnabled ?? false;

  const open = showLoginModal && loginModalContext === 'support';

  const handleOpenChange = (next: boolean) => {
    if (!next) closeLoginModal();
  };

  const handleLogin = () => {
    closeLoginModal();
    openLoginModal(SUPPORT_REDIRECT); // riapre LoginModal normale (senza context)
  };

  const handleRegister = () => {
    closeLoginModal();
    navigate(`/register?redirect=${encodeURIComponent(SUPPORT_REDIRECT)}`);
  };

  const handleFaq = () => {
    closeLoginModal();
    navigate('/faq');
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalPortal>
        <ModalOverlay className="bg-[#231F55]/30 backdrop-blur-lg" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] max-w-[440px] translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl focus:outline-none"
          aria-labelledby="support-gate-title"
        >
          <ModalClose className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus:ring-2 focus:ring-brand-teal/40">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Chiudi</span>
          </ModalClose>

          <div className="flex flex-col items-center px-7 py-9 text-center">
            <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-sky">
              <svg aria-hidden="true" className="h-7 w-7 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </span>

            <ModalTitle id="support-gate-title" className="text-xl font-semibold tracking-tight text-neutral-900">
              Contatta l&apos;assistenza
            </ModalTitle>
            <ModalDescription className="mt-1.5 text-sm text-neutral-600">
              Accedi o registrati per scriverci.
            </ModalDescription>

            <button
              type="button"
              onClick={handleLogin}
              className="mt-6 flex h-11 w-full items-center justify-center rounded-lg bg-brand-orange px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange/90 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
              autoFocus
            >
              Accedi
            </button>

            {registrationOpen && (
              <>
                <button
                  type="button"
                  onClick={handleRegister}
                  className="mt-3 flex h-11 w-full items-center justify-center rounded-lg border border-neutral-300 bg-white px-5 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40"
                >
                  Registrati
                </button>
                <p className="mt-2 text-xs text-neutral-500">
                  La registrazione è gratuita e richiede solo un&apos;email.
                </p>
              </>
            )}

            <button
              type="button"
              onClick={handleFaq}
              className="mt-5 text-xs text-neutral-500 underline-offset-2 transition-colors hover:text-brand-teal hover:underline"
            >
              Hai una domanda veloce? Consulta le FAQ
            </button>
          </div>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
}
